/**
 * connectAsync — connexion Bluetooth SPP sans bloquer l'écran.
 *
 * `VisionPosBT.connect` est une méthode Java synchrone : tant qu'Android n'a pas
 * rendu la main, le fil JS de la WebView est figé (ni toucher, ni rendu, ni
 * temps réel). Sur une imprimante injoignable, cela dure 8 à 14 s PAR BON
 * (BUTCHER'S LA SOIE 2026-11-01 : relances de la tablette en retard de 13 s,
 * exactement pendant les échecs BT).
 *
 * Les APK récentes exposent `connectAsync(mac, requestId)` : la connexion part
 * sur un fil natif, le résultat revient par CustomEvent (même principe que
 * `scanAsync`). Les APK plus anciennes n'ont pas la méthode : l'appelant reste
 * alors sur le `connect` synchrone, à l'identique.
 */

export const BT_CONNECT_RESULT_EVENT = 'vp-bt-connect-result';

/** Garde-fou : Android rend la main en 8 à 14 s, un événement perdu ne doit pas bloquer la file. */
export const BT_CONNECT_TIMEOUT_MS = 30_000;

type NativeAsyncConnect = {
  connectAsync?: (mac: string, requestId: string) => unknown;
};

type ConnectResultDetail = {
  requestId?: string;
  ok?: boolean;
};

let requestSeq = 0;

export function supportsAsyncConnect(bridge: NativeAsyncConnect): boolean {
  return typeof bridge.connectAsync === 'function';
}

export function connectWithoutBlocking(
  bridge: NativeAsyncConnect,
  mac: string,
  timeoutMs: number = BT_CONNECT_TIMEOUT_MS,
): Promise<void> {
  requestSeq += 1;
  const requestId = `bt-connect-${Date.now()}-${requestSeq}`;

  return new Promise<void>((resolve, reject) => {
    let timer = 0;
    const onResult = (event: Event) => {
      const detail = (event as CustomEvent<ConnectResultDetail>).detail;
      if (detail?.requestId !== requestId) return;
      window.clearTimeout(timer);
      window.removeEventListener(BT_CONNECT_RESULT_EVENT, onResult);
      if (detail.ok) resolve();
      else reject(new Error('Connexion BT échouée'));
    };
    const fail = (error: Error) => {
      window.clearTimeout(timer);
      window.removeEventListener(BT_CONNECT_RESULT_EVENT, onResult);
      reject(error);
    };

    timer = window.setTimeout(
      () => fail(new Error('Connexion BT échouée (délai dépassé)')),
      timeoutMs,
    );

    // Écoute posée AVANT l'appel natif : aucun résultat ne peut être manqué.
    window.addEventListener(BT_CONNECT_RESULT_EVENT, onResult);
    try {
      bridge.connectAsync?.(mac, requestId);
    } catch (e) {
      fail(e instanceof Error ? e : new Error('Connexion BT échouée'));
    }
  });
}
